// Admin-only mutations. Not exposed via the public `services/index.js`
// so the surface is clear and imports are grep-able.

import { normalizeDigitsDeep } from "@/utils/normalizeDigits";

const RAW_BASE = process.env.REACT_APP_BACKEND_URL;
const useRelative =
  typeof window !== "undefined" &&
  (!RAW_BASE || window.location.origin === RAW_BASE.replace(/\/$/, ""));
const API = useRelative ? "/api" : `${(RAW_BASE || "").replace(/\/$/, "")}/api`;

const send = async (path, { method = "GET", body, signal } = {}) => {
  const res = await fetch(`${API}${path}`, {
    method,
    // Send httpOnly session cookies so /api/admin/* endpoints receive
    // the auth cookies set by /api/auth/login.
    credentials: "include",
    headers: { "Accept": "application/json", "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`${method} ${path} failed: ${res.status} ${text}`);
    err.status = res.status;
    throw err;
  }
  // Content-type: application/json responses are normalised to Latin digits
  // (frontend-only presentation — API data stays untouched).
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) return null;
  const data = await res.json();
  return normalizeDigitsDeep(data);
};

/** All opportunities regardless of status — the admin queue view. */
export const adminListOpportunities = (opts) =>
  send("/admin/opportunities", opts);

/** Full detail (includes children) for the editor form. */
export const adminGetOpportunity = (publicId, opts) =>
  send(`/admin/opportunities/${encodeURIComponent(publicId)}`, opts);

/** Partial update. Send only the fields that changed. */
export const adminPatchOpportunity = (publicId, payload, opts) =>
  send(`/admin/opportunities/${encodeURIComponent(publicId)}`, {
    method: "PATCH",
    body: payload,
    ...(opts || {}),
  });

export const adminPublishOpportunity = (publicId, opts) =>
  send(`/admin/opportunities/${encodeURIComponent(publicId)}/publish`, {
    method: "POST",
    ...(opts || {}),
  });

export const adminRejectOpportunity = (publicId, opts) =>
  send(`/admin/opportunities/${encodeURIComponent(publicId)}/reject`, {
    method: "POST",
    ...(opts || {}),
  });

/* ─── Admin: Feed sources ──────────────────────────────────────── */

export const adminListSources = (opts) => send("/admin/sources", opts);
export const adminCreateSource = (payload, opts) => send("/admin/sources", {
  method: "POST", body: payload, ...(opts || {}),
});
export const adminUpdateSource = (id, payload, opts) => send(`/admin/sources/${encodeURIComponent(id)}`, {
  method: "PATCH", body: payload, ...(opts || {}),
});
export const adminDeleteSource = (id, opts) => send(`/admin/sources/${encodeURIComponent(id)}`, {
  method: "DELETE", ...(opts || {}),
});
export const adminRefreshSource = (id, opts) => send(`/admin/sources/${encodeURIComponent(id)}/refresh`, {
  method: "POST", ...(opts || {}),
});
export const adminRefreshAllSources = (opts) => send("/admin/sources/refresh-all", {
  method: "POST", ...(opts || {}),
});

/* ─── Admin: Automation registry (Source Intelligence) ─────────── */

export const adminGetSourcesRegistry = (opts) => send("/admin/automation/registry", opts);
export const adminGetSourcesRegistryOrphans = (opts) => send("/admin/automation/registry/orphans", opts);

/* ─── Admin: Feed items ────────────────────────────────────────── */

export const adminListFeedItems = ({ sourceId, status, limit = 50 } = {}, opts) => {
  const qs = new URLSearchParams();
  if (sourceId) qs.set("source_id", sourceId);
  if (status)   qs.set("status", status);
  qs.set("limit", String(limit));
  return send(`/admin/feed-items?${qs.toString()}`, opts);
};
export const adminIgnoreFeedItem = (id, opts) => send(`/admin/feed-items/${encodeURIComponent(id)}/ignore`, {
  method: "POST", ...(opts || {}),
});
export const adminAnalyzeFeedItem = (id, opts) => send(`/admin/feed-items/${encodeURIComponent(id)}/analyze`, {
  method: "POST", ...(opts || {}),
});
